import { withCreds } from '../authService';
import { inferenceBase } from '../config/apiBase';
import { parseVoiceServerTimingHeader } from './voiceLatency';

function parseMaybeJson(raw) {
  const text = String(raw || '').trim();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function firstNonEmptyString(values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function normalizeResponse(parsed, raw) {
  const obj = parsed && typeof parsed === 'object' ? parsed : {};

  let normalized = firstNonEmptyString([
    obj.response,
    obj.reply,
    obj.result,
    obj.text,
    obj.message,
    obj.ai_response,
    obj.raw_ai,
    obj.output,
    obj.content,
    obj.generated_text,
    obj.assistant_response,
  ]);

  if (normalized) return normalized;

  normalized = firstNonEmptyString([
    obj.data?.response,
    obj.data?.reply,
    obj.data?.result,
    obj.data?.text,
    obj.data?.message,
    obj.data?.ai_response,
    obj.data?.raw_ai,
    obj.data?.output,
    obj.data?.content,
    obj.data?.generated_text,
    obj.payload?.response,
    obj.payload?.reply,
    obj.payload?.text,
    obj.payload?.message,
    obj.payload?.generated_text,
    obj.choices?.[0]?.message?.content,
    obj.choices?.[0]?.text,
  ]);

  if (normalized) return normalized;

  const rawText = String(raw || '').trim();
  if (rawText && !(rawText.startsWith('{') || rawText.startsWith('['))) {
    return rawText;
  }

  return '';
}

function makeClientMessageId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function callInference(prompt, options = {}) {
  const endpoint = `${inferenceBase}/v1/chat/completions`;

  const body = {
    model: options.model || undefined,
    messages: [
      {
        role: 'user',
        content: prompt,
      },
    ],
    max_tokens: options.maxTokens ?? 120,
    temperature: 0.0,
    top_p: 1.0,
  };

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.REACT_APP_ALLOWED_TOKEN || 'brightresolve-local-token'}`,
    },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  const raw = await res.text().catch(() => '');
  const parsed = parseMaybeJson(raw);
  const normalized = normalizeResponse(parsed, raw);

  console.info('[sendMessage] transport completed', {
    source: 'inference',
    status: res.status,
  });

  if (!res.ok) {
    throw new Error(normalized || `Inference request failed with status ${res.status}`);
  }

  if (!normalized) {
    throw new Error('Inference produced no reply.');
  }

  return {
    raw,
    parsed,
    normalized,
    source: 'inference',
  };
}

async function callApiFallback(prompt, options = {}) {
  const res = await withCreds('/process', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      session_id: options.sessionId || undefined,
      thread_id: options.threadId || undefined,
      edited_message_id: options.editedMessageId || undefined,
      server: true,
      webgpu: false,
      force_server: true,
      max_tokens: options.maxTokens ?? 120,
      voice_mode: options.voiceMode === true,
      voice_interruption: options.voiceInterruption === true,
      voice_session_id: options.voiceSessionId || undefined,
      voice_generation_id: options.voiceGenerationId ?? undefined,
      voice_incident_ref: options.voiceIncidentRef || undefined,
      client_message_id: makeClientMessageId(),
    }),
    signal: options.signal,
  });

  options.onResponseHeaders?.();

  const raw = await res.text().catch(() => '');
  options.onResponseBody?.();
  const parsed = parseMaybeJson(raw);
  const normalized = normalizeResponse(parsed, raw);
  const turnAction = options.voiceMode === true && parsed?.turn_action === 'hold_floor'
    ? 'hold_floor'
    : 'assistant_response';

  console.info('[sendMessage] transport completed', {
    source: 'api-fallback',
    status: res.status,
  });

  if (!res.ok) {
    const error = new Error(normalized || `API fallback failed with status ${res.status}`);
    error.status = res.status;
    error.requestId = res.headers?.get?.('X-Request-ID') || '';
    throw error;
  }

  if (!normalized && turnAction !== 'hold_floor') {
    throw new Error('API fallback produced no reply.');
  }

  return {
    raw,
    parsed,
    normalized,
    turnAction,
    source: 'api-fallback',
    requestId: res.headers?.get?.('X-Request-ID') || '',
    buildSha: res.headers?.get?.('X-BR-Build') || '',
    serverTiming: parseVoiceServerTimingHeader(res.headers?.get?.('Server-Timing')),
  };
}

export async function sendMessage(text, options = {}) {
  const prompt = String(text || '').trim();

  if (!prompt) {
    return {
      raw: '',
      parsed: {},
      normalized: '',
      source: 'none',
    };
  }

  const {
    allowApiFallback = true,
    model,
    maxTokens,
  } = options;

  try {
    return await callApiFallback(prompt, options);
  } catch (apiErr) {
    console.warn('[sendMessage] API request failed');
    throw apiErr;
  }
}

export default sendMessage;
